import axiosPackage from 'axios';
import { AxiosError, AxiosRequestConfig } from 'axios';
import {
  getStoredClientLocation,
  encodeClientLocationForHeader,
} from './client-geolocation';

// Access token key - match with auth-context
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const CURRENT_TENANT_ID_KEY = 'current_tenant_id';

// Token utility functions
const getAccessToken = (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY);
const getRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY);
const setAccessToken = (token: string) => localStorage.setItem(ACCESS_TOKEN_KEY, token);
const setRefreshToken = (token: string) => localStorage.setItem(REFRESH_TOKEN_KEY, token);
const getCurrentTenantId = (): string | null => localStorage.getItem(CURRENT_TENANT_ID_KEY);
const setCurrentTenantId = (tenantId: string) => localStorage.setItem(CURRENT_TENANT_ID_KEY, tenantId);
const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(CURRENT_TENANT_ID_KEY);
};

// Helper function to clear auth data and redirect to login
const clearAuthAndRedirect = () => {
  clearTokens();
  window.location.href = '/sign-in';
};

// Create axios instance
const axios = axiosPackage.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5074',
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  },
  // Accept all status codes - don't throw on 4xx/5xx errors
  // This allows us to handle API error messages properly without try-catch
  validateStatus: () => true,
});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: { resolve: (value?: unknown) => void; reject: (reason?: any) => void }[] = [];

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add a request interceptor
axios.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const currentTenantId = getCurrentTenantId();
    if (currentTenantId) {
      config.headers['X-Tenant-Id'] = currentTenantId;
    }
    // Optional: client geolocation for session audit (Base64 to avoid non-ASCII in header)
    const clientLocation = getStoredClientLocation();
    if (clientLocation) {
      const encoded = encodeClientLocationForHeader(clientLocation);
      if (encoded) config.headers['X-Client-Location'] = encoded;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor
axios.interceptors.response.use(
  async (response) => {
    const originalRequest = response.config as AxiosRequestConfig & { _retry?: boolean };
    
    // Handle 401 Unauthorized errors (now in success handler since validateStatus accepts all)
    if (response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If a refresh is already in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          clearAuthAndRedirect();
          return Promise.reject(new Error('No refresh token available'));
        }

        const refreshResponse = await axios.post('/api/Auth/refresh-token', {
          refreshToken: refreshToken
        });

        if (refreshResponse.data.success && refreshResponse.data.data) {
          const { token, refreshToken: newRefreshToken } = refreshResponse.data.data;
          
          // Store both tokens
          setAccessToken(token);
          if (newRefreshToken) {
            setRefreshToken(newRefreshToken);
          }

          // Update Authorization header
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }

          // Process queued requests
          processQueue(null, token);

          return axios(originalRequest);
        } else {
          processQueue(new Error('Failed to refresh token'));
          clearAuthAndRedirect();
          return Promise.reject(new Error('Failed to refresh token'));
        }
      } catch (refreshError) {
        processQueue(refreshError);
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 Forbidden errors
    if (response.status === 403) {
      clearAuthAndRedirect();
      return Promise.reject(new Error('Không có quyền truy cập'));
    }

    return response;
  },
  async (error: AxiosError) => {
    // This should rarely be called now since validateStatus accepts all
    // But keeping it for network errors, timeouts, etc.
    return Promise.reject(error);
  }
);

export { axios, getAccessToken, getRefreshToken, setAccessToken, setRefreshToken, clearTokens, getCurrentTenantId, setCurrentTenantId }; 